"use client";

import { useMemo } from "react";
import Image from "next/image";
import AppButton from "@/components/shared/app-button/AppButton";
import {
  getHttpErrorIllustrationForStatus,
  getHttpErrorIllustrationForUnknownError,
} from "@/lib/httpStatusError";

type HttpErrorIllustrationProps = {
  error?: unknown;
  /**
   * Prefer this when rendering from a Server Component after catching an HTTP error.
   * `Error` props are redacted in production builds, so status would otherwise fall back to 500.
   */
  statusCode?: number;
  /** Defaults to full navigation home (resets client state). */
  onGoHome?: () => void;
};

export default function HttpErrorIllustration({
  error,
  statusCode,
  onGoHome,
}: HttpErrorIllustrationProps) {
  const visual = useMemo(() => {
    if (typeof statusCode === "number") {
      return getHttpErrorIllustrationForStatus(statusCode);
    }
    return getHttpErrorIllustrationForUnknownError(error);
  }, [error, statusCode]);

  const goHome =
    onGoHome ??
    (() => {
      window.location.assign("/");
    });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
      <Image
        src={visual.src}
        alt={visual.alt}
        width={600}
        height={600}
        className="mb-12 max-w-full"
        priority
      />
      <AppButton
        onClick={goHome}
        label="Back to homepage"
        className="text-sm lg:text-sm"
      />
    </div>
  );
}
